"use client";

import { ConfirmationContent } from "@/app/_components/ConfirmationPayment";
import { PaymentForm } from "@/app/_components/PaymentForm";
import { PricingPlans } from "@/app/_components/PricingPlans";
import { createClient } from "@/prismicio";
import { useCallback, useEffect, useMemo, useState } from "react";
import { HiOutlineChevronRight } from "react-icons/hi2";
import cn from "@/utils/cn";
import Image from "next/image";
import { useZStore } from "@/utils/z-store";
import CloseIcon from "../../_components/modals/assets/close-icon.svg";
import { PaymentLayout } from "@/app/_components/PaymentLayout"; // Import the new layout
import TextComponent from "@/app/_components/TextComponent";

type Step = "selection" | "payment" | "confirmation";

interface OrderDetails {
  orderNumber: string;
  email: string;
  subscriptionId?: string;
  activationDate?: string;
}

const usePricingData = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const client = createClient();
        const content = await client.getSingle("pricing_page");
        const pricingSlice = content.data.slices.find(
          (slice: any) => slice.slice_type === "pricing",
        );
        setData(pricingSlice?.items || []);
      } catch (err) {
        setError("Failed to fetch pricing data.");
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  return { data, loading, error };
};

export default function BillingModalContent() {
  const [step, setStep] = useState<Step>("selection");
  const [selectedPlan, setSelectedPlan] = useState<string>("Standard_MONTHLY"); // Mock selection
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [showFeatureMatrix, setShowFeatureMatrix] = useState(true);
  const [promoCode, setPromoCode] = useState<string | undefined>(undefined);
  const [appliedCoupon, setAppliedCoupon] = useState<any | null>(null);

  const { data: pricingItems, loading, error } = usePricingData();
  const { actions } = useZStore();

  const handlePlanSelection = useCallback(
    (plan: string, billingCycle: "MONTHLY" | "ANNUAL") => {
      setSelectedPlan(`${plan}_${billingCycle}`);
      setStep("payment");
    },
    [],
  );

  const handlePaymentSuccess = useCallback((details: OrderDetails) => {
    // Add current date as activation date if not provided
    const orderDetailsWithActivation = {
      ...details,
      activationDate: details.activationDate || new Date().toISOString(),
    };
    setOrderDetails(orderDetailsWithActivation);
    setStep("confirmation");
  }, []);

  const planDetails = useMemo(() => {
    if (!selectedPlan || !pricingItems.length)
      return {
        name: "Standard",
        price: 252.0,
        cycle: "MONTHLY" as "MONTHLY" | "ANNUAL",
      };

    const [planName, billingCycle] = selectedPlan.split("_");

    // Map plan names to array indices (assuming order: Basic, Standard, Premium)
    const planIndex =
      planName === "BASIC" ? 0 : planName === "STANDARD" ? 1 : 2;
    const planData = pricingItems[planIndex];

    if (!planData)
      return {
        name: "Standard",
        price: 252.0,
        cycle: "MONTHLY" as "MONTHLY" | "ANNUAL",
      };

    // Extract price from Prismic rich text format
    const price =
      billingCycle === "ANNUAL"
        ? parseFloat(planData.annual_price[0]?.text || "0")
        : parseFloat(planData.monthly_price[0]?.text || "0");

    // Extract plan name from Prismic rich text format
    const displayName = planData.title[0]?.text || planName;

    return {
      name: displayName,
      price: price,
      cycle: billingCycle as "MONTHLY" | "ANNUAL",
    };
  }, [selectedPlan, pricingItems]);

  const stepTitle = useMemo(() => {
    switch (step) {
      case "selection":
        return "Upgrade Your Plan";
      case "payment":
        return "Payment";
      case "confirmation":
        return "Confirmation";
      default:
        return "";
    }
  }, [step]);

  if (loading) return <div className="py-20 text-center">Loading...</div>;
  if (error)
    return <div className="py-20 text-center text-red-500">{error}</div>;

  return (
    <main
      // className={cn(
      //   "relative my-10 w-[550px] lg:w-[600px] max-w-[1494px] rounded-[20px] bg-gray-50 px-[43px] py-[36px] font-['Poppins'] shadow-[0px_4px_20px_0px_rgba(142,142,147,0.50)] 2xl:w-[1494px] 2xl:px-[112px] 2xl:py-[69px] xl:w-[1110px] xl:h-[653px]",
      // )}
      className={cn(
        "relative my-10 w-[263px] md:w-[550px] lg:w-[600px] max-w-[1494px] rounded-[20px] bg-gray-50 px-[43px] py-[36px] font-['Poppins'] shadow-[0px_4px_20px_0px_rgba(142,142,147,0.50)] xl:w-[1110px] 2xl:w-[1494px] xl:px-[112px] xl:py-[69px]",
      )}
    >
      <div
        onClick={() => actions.closeModal()}
        className="absolute right-4 top-4 z-10 cursor-pointer"
      >
        <Image src={CloseIcon} alt="close icon" className="hover:opacity-60" />
      </div>

      <TextComponent type="h2" className="text-kd-lte-blue">
        {stepTitle}
      </TextComponent>
      {step === "confirmation" && (
        <p className="mt-2 font-semibold text-green-500">
          Successfully activated!
        </p>
      )}

      {step === "selection" && (
        <PricingPlans
          onSelectPlan={handlePlanSelection}
          items={pricingItems}
          showFeatureMatrix={showFeatureMatrix}
          setShowFeatureMatrix={setShowFeatureMatrix}
        />
      )}

      {step === "payment" && selectedPlan && (
        <PaymentLayout
          step="payment"
          planName={planDetails.name}
          price={planDetails.price}
          billingCycle={planDetails.cycle}
          onNavigateToSelection={() => setStep("selection")}
          onPromoCodeApplied={(coupon: any) => {
            setAppliedCoupon(coupon);
            setPromoCode(coupon.id);
          }}
          appliedCoupon={appliedCoupon}
        >
          <PaymentForm
            plan={planDetails.name}
            billingCycle={planDetails.cycle}
            price={planDetails.price}
            onSuccess={handlePaymentSuccess}
            promoCode={promoCode}
          />
        </PaymentLayout>
      )}

      {step === "confirmation" && orderDetails && (
        <PaymentLayout
          step="confirmation"
          planName={planDetails.name}
          price={planDetails.price}
          billingCycle={planDetails.cycle}
          orderNumber={orderDetails.orderNumber}
          activationDate={orderDetails.activationDate}
        >
          <ConfirmationContent email={orderDetails.email} />
        </PaymentLayout>
      )}
    </main>
  );
}
